import { Injectable } from '@angular/core';
import {
	CAPACITY_SEARCH_FILTER,
	CARGOCHIEF_KEYS,
	RANGE_FILTERS,
} from '@domain/constants/app-variables';
import {
	CarrierDTOResponse,
	TruckSearchTable,
	IS_NOT_NULL_OR_UNDEFINED,
} from 'cargo-chief-commons';
import { UtilService } from '@service/util/util.service';
import { Cargochief } from '../cargochief/cargochief.service';
import { AccountSettingsService } from '../accountSettings/account-settings.service';

@Injectable({
	providedIn: 'root',
})
export class CapacitySearchFilterService {
	constructor(
		public utilService: UtilService,
		private cargochief: Cargochief,
		private accountSettings: AccountSettingsService
	) {}

	applyFilter(
		carrierSelected: any,
		truckFiltersSelected: any,
		truckSearchResult: Array<TruckSearchTable>,
		sortColumn: string,
		carrierHQList: Array<CarrierDTOResponse>
	) {
		truckSearchResult = [...this.applyCarrierSelectedFilter(carrierSelected, truckSearchResult)];
		if (truckFiltersSelected.length > 0) {
			truckSearchResult = [...this.applyTruckFilters(truckFiltersSelected, truckSearchResult)];
		}
		if (!this.utilService.isNullOrUndefined(carrierHQList) && carrierHQList.length > 0) {
			let result: Array<TruckSearchTable> = [];
			carrierHQList.map((carrier) => {
				const search: TruckSearchTable = truckSearchResult.filter((csearch) => {
					return csearch.carrierId == carrier.id;
				})[0];
				if (!this.utilService.isNullOrUndefined(search)) {
					result.push(search);
				}
			});
			truckSearchResult = [...result];
		}
		return this.sortByColumn(sortColumn, truckSearchResult);
	}

	applyCarrierSelectedFilter(
		carrierSelected,
		filteredResult: Array<TruckSearchTable>
	): Array<TruckSearchTable> {
		switch (carrierSelected) {
			case CAPACITY_SEARCH_FILTER.CARRIERS[0]:
				return filteredResult;
				break;
			case CAPACITY_SEARCH_FILTER.CARRIERS[1]:
				return filteredResult.filter((search: TruckSearchTable) => {
					if (search.customerCarrier) {
						return search;
					}
				});
			default:
				return filteredResult;
		}
	}

	getMinDHD(truckSearchResult: Array<TruckSearchTable>): number {
		let result = null;
		truckSearchResult.map((search: TruckSearchTable) => {
			if (!this.utilService.isNullOrUndefined(search.destinationDeadhead)) {
				if (result == null) {
					result = Math.round(search.destinationDeadhead);
				} else if (Math.round(search.destinationDeadhead) < result) {
					result = Math.round(search.destinationDeadhead);
				}
			}
		});
		return result;
	}

	getMaxMatch(truckSearchResult: Array<TruckSearchTable>): number {
		let result = null;
		truckSearchResult.map((search: TruckSearchTable) => {
			if (!this.utilService.isNullOrUndefined(search.match)) {
				if (result == null) {
					result = Math.round(search.match);
				} else if (Math.round(search.match) > result) {
					result = Math.round(search.match);
				}
			}
		});
		return result;
	}

	sortSearchResultByMatch(truckSearchResult: Array<TruckSearchTable>): Array<TruckSearchTable> {
		return truckSearchResult.sort(
			(a, b) => b.match - a.match || a.businessName.localeCompare(b.businessName)
		);
	}

	sortSearchResultByDistance(truckSearchResult: Array<TruckSearchTable>): Array<TruckSearchTable> {
		return truckSearchResult.sort(
			(a, b) => a.distance - b.distance || a.businessName?.localeCompare(b?.businessName)
		);
	}

	sortSearchResultDHD(truckSearchResult: Array<TruckSearchTable>): Array<TruckSearchTable> {
		let withdhd = [],
			withoutdhd = [];
		truckSearchResult.map((search) => {
			if (this.utilService.isNullOrUndefined(search.destinationDeadhead)) {
				withoutdhd.push(search);
			} else {
				if (search.isLane) {
					if (search.lanePath.split(' -> ')[search.lanePath.split(' -> ').length - 1].length > 2) {
						withdhd.push(search);
					} else {
						withoutdhd.push(search);
					}
				} else if (!search.isLane) {
					withdhd.push(search);
				}
			}
		});
		withdhd = withdhd.sort(
			(a, b) =>
				b.destinationDeadhead - a.destinationDeadhead ||
				a.businessName?.localeCompare(b?.businessName)
		);
		return withdhd.concat(withoutdhd);
	}

	getMaxDHO(truckSearchResult: Array<TruckSearchTable>): number {
		let result = null;
		truckSearchResult.map((search: TruckSearchTable) => {
			if (!this.utilService.isNullOrUndefined(search.originDeadhead)) {
				if (result == null) {
					result = Math.round(search.originDeadhead);
				} else if (Math.round(search.originDeadhead) > result) {
					result = Math.round(search.originDeadhead);
				}
			}
		});
		return result;
	}

	getMaxDHD(truckSearchResult: Array<TruckSearchTable>): number {
		let result = null;
		truckSearchResult.map((search: TruckSearchTable) => {
			if (!this.utilService.isNullOrUndefined(search.destinationDeadhead)) {
				if (result == null) {
					result = Math.round(search.destinationDeadhead);
				} else if (Math.round(search.destinationDeadhead) > result) {
					result = Math.round(search.destinationDeadhead);
				}
			}
		});
		return result;
	}

	sortSearchResultByFrequency(truckSearchResult: Array<TruckSearchTable>): Array<TruckSearchTable> {
		const newSearch = truckSearchResult.map(({ businessName, frequency, match }, i) => {
			let formattedFrequency = frequency === 'Daily' ? 7 : 0;

			if (!formattedFrequency && !isNaN(Number(frequency))) {
				formattedFrequency = Number(frequency);
			}

			if (!formattedFrequency) {
				formattedFrequency = frequency.split(',').length;
			}

			return { index: i, frequency: formattedFrequency, businessName, match };
		});

		return newSearch
			.sort((a, b) => {
				return (
					b.frequency - a.frequency ||
					b.match - a.match ||
					a.businessName?.localeCompare(b?.businessName)
				);
			})
			.map(({ index }) => truckSearchResult[index]);
	}

	sortByColumn(
		sortColumn: string,
		truckSearchResult: Array<TruckSearchTable>
	): Array<TruckSearchTable> {
		switch (sortColumn) {
			case RANGE_FILTERS.SORT.MATCH:
				return this.sortSearchResultByMatch(truckSearchResult);
			case RANGE_FILTERS.SORT.FREQUENCY:
				return this.sortSearchResultByFrequency(truckSearchResult);
			case RANGE_FILTERS.SORT.DISTANCE_FROM_HQ:
				return this.sortSearchResultByDistance(truckSearchResult);
			default:
				return truckSearchResult;
		}
	}

	applyTruckFilters(
		truckFiltersSelected,
		filteredResult: Array<TruckSearchTable>
	): Array<TruckSearchTable> {
		switch (truckFiltersSelected) {
			case CAPACITY_SEARCH_FILTER.TRUCKS[1]:
				return filteredResult.filter((search: TruckSearchTable) => {
					if (!search.isLane) {
						return search;
					}
				});
			default:
				return filteredResult;
		}
	}

	getDeadhead(): { originDeadhead: number; destinationDeadhead: number } {
		const result = { originDeadhead: null, destinationDeadhead: null };
		const DHO = this.cargochief.getItem(CARGOCHIEF_KEYS.ORIGIN_DEADHEAD_SLIDER),
			DHD = this.cargochief.getItem(CARGOCHIEF_KEYS.DESTINATION_DEADHEAD_SLIDER);
		if (!this.utilService.isNullOrUndefined(DHO)) {
			result.originDeadhead = JSON.parse(DHO);
		} else {
			result.originDeadhead = this.accountSettings.SEARCH_TRUCK_ORIGIN_DEADHEAD_LIMIT;
		}
		if (!this.utilService.isNullOrUndefined(DHD)) {
			result.destinationDeadhead = JSON.parse(DHD);
		} else {
			result.destinationDeadhead = this.accountSettings.SEARCH_TRUCK_DESTINATION_DEADHEAD_LIMIT;
		}
		return result;
	}

	getMinLoadsPerWeek(): number {
		const frequencyFilter = this.cargochief.getItem(CARGOCHIEF_KEYS.FREQUENCY_FILTER);
		if (IS_NOT_NULL_OR_UNDEFINED(frequencyFilter)) {
			const { minLoadsPerWeek } = JSON.parse(frequencyFilter);
			return minLoadsPerWeek;
		}
		return null;
	}
}
